/**
 * <license header>
 */

import RuntimeAction from '../runtime-action';
import RuntimeActionResponse from '../runtime-action/response';
import WebhookActionResponse from './response';
import { HttpMethod } from '../runtime-action/types';
import { RuntimeActionResponseType } from '../runtime-action/response/types';
import { WebhookActionResponseType } from './response/types';
import { SignatureVerification } from './types';
import crypto from 'crypto';
import Validator from '../runtime-action/validator';

/**
 * WebhookAction - Wrapper for RuntimeAction with webhook-specific functionality.
 *
 * Handles Adobe Commerce webhook requests with automatic signature verification,
 * parameter validation, and response formatting.
 */
class WebhookAction {
  /**
   * Execute a webhook action with validation and response handling.
   *
   * @param name - Name of the webhook action
   * @param requiredParams - Required parameters in the webhook payload
   * @param requiredHeaders - Required headers (e.g., signature headers)
   * @param signatureVerification - Enable/disable signature verification
   * @param action - Webhook action function returning WebhookActionResponse
   * @returns Function that handles the webhook HTTP request
   */
  static execute(
    name: string = 'webhook',
    requiredParams: string[] = [],
    requiredHeaders: string[] = [],
    signatureVerification: SignatureVerification = SignatureVerification.DISABLED,
    action: (
      params: { [key: string]: any },
      ctx: { logger: any; headers: { [key: string]: any } }
    ) => Promise<
      WebhookActionResponseType | WebhookActionResponseType[]
    > = async (): Promise<WebhookActionResponseType> => WebhookActionResponse.success()
  ): (params: { [key: string]: any }) => Promise<RuntimeActionResponseType> {
    // Webhooks typically use POST method
    const httpMethods: HttpMethod[] = [HttpMethod.POST];

    // Verify webhook signature using a public key and SHA256.
    const verifySignature = async (params: { [key: string]: any }): Promise<string | null> => {
      const signature = params.__ow_headers['x-adobe-commerce-webhook-signature'] || '';
      if (!signature) {
        return 'Header `x-adobe-commerce-webhook-signature` not found. Make sure Webhooks signature is enabled in the Commerce instance.';
      }

      const body = params.__ow_body;
      if (!body) {
        return 'Request body not found. Make sure the action is configured with `raw-http: true`.';
      }

      let publicKey = params.PUBLIC_KEY;
      if (!publicKey && params.PUBLIC_KEY_BASE64) {
        publicKey = atob(params.PUBLIC_KEY_BASE64);
      }

      if (!publicKey) {
        return 'Public key not found. Make sure the action is configured with the input `PUBLIC_KEY` or `PUBLIC_KEY_BASE64` and it is defined in .env file.';
      }

      try {
        const verifier = crypto.createVerify('SHA256');
        verifier.update(body);
        const isSignatureValid = verifier.verify(publicKey, signature, 'base64');

        if (!isSignatureValid) {
          return 'The signature is invalid.';
        }
      } catch (error) {
        return 'The signature is invalid.';
      }
      return null;
    };

    // Create a callback that wraps the webhook action response
    const callback = async (
      params: { [key: string]: any },
      ctx: { logger: any; headers: { [key: string]: any } }
    ): Promise<RuntimeActionResponseType> => {
      if (signatureVerification === SignatureVerification.ENABLED) {
        const verificationErrorMessage = await verifySignature(params);
        if (verificationErrorMessage) {
          const verificationErrorResponse =
            WebhookActionResponse.exception(verificationErrorMessage);
          return RuntimeActionResponse.success(JSON.stringify(verificationErrorResponse));
        }

        params = {
          ...params,
          ...JSON.parse(atob(params.__ow_body)),
        };
      }

      // check for missing request input parameters and headers
      const errorMessage =
        Validator.checkMissingRequestInputs(params, requiredParams, requiredHeaders) ?? '';
      if (errorMessage) {
        const errorMessageResponse = WebhookActionResponse.exception(errorMessage);
        return RuntimeActionResponse.success(JSON.stringify(errorMessageResponse));
      }

      // Execute the webhook action
      const response = await action(params, ctx);

      // Wrap the webhook response in a successful HTTP response
      return RuntimeActionResponse.success(JSON.stringify(response));
    };

    // Delegate to RuntimeAction with webhook-specific defaults
    return RuntimeAction.execute(name, httpMethods, [], [], callback);
  }
}

export default WebhookAction;
